import './styles.scss'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Empty, Row } from 'antd'

import B2becPagination from '@/components/B2becPagination/B2becPagination'
import B2becSpinner from '@/components/B2becSpinner'
import CustomButton from '@/components/CustomButton'
import { PlusIcon } from '@/components/Icons'
import ProductCard from '@/components/ProductCard'
import B2becCardSkeleton from '@/components/Skeletons/B2becCardSkeleton'
import { ACTION_STATUS, PAGE_SIZE } from '@/libs/constants'
import { isValidArray } from '@/libs/utils/array'

interface ProductListProps {
  classNames?: string
  isLoading?: boolean
  productList?: Record<string, unknown>[]
  onSelectMachineHandler?: (item: Record<string, unknown>) => void
  isMachineSelection?: boolean
}

const ProductList = ({
  classNames = null,
  isLoading = false,
  productList,
  isMachineSelection = false,
  onSelectMachineHandler,
}: ProductListProps) => {
  const { t } = useTranslation()
  const elementRef = useRef([])

  const [isVerifyingMachine, setIsVerifyingMachine] = useState(false)

  return (
    <div className={classNames}>
      {
        // add loading behavior while verifying product
        // prevent user clicks on other products and process verification when the first validation is not finished
        isVerifyingMachine && <B2becSpinner isLoading={isVerifyingMachine} />
      }

      {isLoading
        ? (
          <B2becCardSkeleton
            columns={{ xs: 24, sm: 24, md: 12, lg: 6, xl: 6, xxl: 6 }}
            gutter={[16, 16]}
            hasPrice={false}
            imageHeight={530}
            number={4}
          />
        )
        : isValidArray(productList)
        ? (
          <Row
            className="product-card__wrapper"
            gutter={[16, 16]}
            style={{ marginTop: 30 }}
          >
            {productList.map((item, index) => (
              <Col
                key={`${String(item?.materialNumber ?? item?.materialId ?? '')}-${
                  String(item?.salesOrderNumber ?? '')
                }-${String(item?.serialNumber ?? '')}`}
                lg={6}
                md={6}
                sm={24}
                xl={6}
                xs={24}
                id={`${String(item?.materialNumber ?? item?.materialId ?? '')}-${
                  String(item?.salesOrderNumber ?? '')
                }`}
                className="card-item"
                // store every elements to the ref variable
                ref={(element) => {
                  elementRef.current[index] = element
                }}
              >
                <ProductCard
                  date={item?.orderDate}
                  img={item?.imgUrl}
                  isMachineSelection={isMachineSelection}
                  materialId={item?.materialNumber}
                  name={item?.name}
                  salesOrderNumber={item?.salesOrderNumber}
                  setVerifyStatus={setIsVerifyingMachine}
                  category={item?.categoryName}
                  // pass the ref to the child component
                  ref={elementRef}
                  selectMachineHandler={() => onSelectMachineHandler?.(item)}
                  // options
                  serialNumber={item?.serialNumber}
                />
              </Col>
            ))}
          </Row>
        )
        : <Empty description={t('noData')} />}
    </div>
  )
}

interface ProductListWithPaginationProps {
  classNames?: string
  isLoading?: boolean
  total?: number
  actionStatus?: string
  listData?: Record<string, unknown>[]
  onSelectMachineHandler?: (item: Record<string, unknown>) => void
  isMachineSelection?: boolean
  isMobile?: boolean
  getListHandler?: (currentPage: number, pageSize: number) => void
}

const ProductListWithPagination = ({
  classNames = null,
  isLoading = false,
  total = 0,
  actionStatus = ACTION_STATUS.IDLE,
  listData,
  onSelectMachineHandler,
  isMachineSelection = false,
  isMobile = false,
  getListHandler,
}: ProductListWithPaginationProps) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [productList, setProductList] = useState([])

  const { t } = useTranslation()

  const isMobileRef = useRef(null)

  const handlePageChange = useCallback((current) => {
    setCurrentPage(current)
  }, [])

  useEffect(() => {
    const pageSize = isMobile ? PAGE_SIZE[4] : PAGE_SIZE[12]
    getListHandler?.(currentPage, pageSize)
  }, [currentPage, isMobile, getListHandler])

  // for the desktop version, we still use the pagination normally
  // for the mobile version, pagination won't be applied anymore
  // the new products will keep expanding to the current list
  useEffect(() => {
    // make sure api finished calling
    if (actionStatus === ACTION_STATUS.SUCCESS) {
      // initial the ref variable at the first time
      if (isMobileRef.current === null) {
        isMobileRef.current = isMobile
      }

      if (isMobile) {
        setProductList((previousState) => {
          // when changing device from desktop to mobile
          // current listData still contains 12 items, so it need to be clear
          // if it is not the changing case, just push new items to current list
          return isMobile === isMobileRef.current
            ? [...previousState, ...listData]
            : []
        })
        isMobileRef.current = true
      } else {
        setProductList(listData)
        isMobileRef.current = false
      }
    }
  }, [isMobile, listData, actionStatus])

  return (
    <>
      <ProductList
        classNames={classNames}
        isLoading={isLoading}
        isMachineSelection={isMachineSelection}
        productList={productList}
        onSelectMachineHandler={onSelectMachineHandler}
      />

      {isMobile
        ? (
          <div className="expand__wrapper">
            <CustomButton
              buttonSize="default"
              className="expand__button"
              type="ghost"
              onClick={() => handlePageChange(+(currentPage + 1))}
            >
              {t('productSubCategoryOverview.button.seeMore')}
              <PlusIcon />
            </CustomButton>
          </div>
        )
        : (
          <B2becPagination
            current={currentPage}
            pageSize={PAGE_SIZE[12]}
            total={total}
            onPageChange={handlePageChange}
          />
        )}
    </>
  )
}

export default ProductListWithPagination
